/* PWA install promotion (Budli)
 * - Android/Chrome: captures beforeinstallprompt, shows branded banner, triggers native install sheet
 * - iOS (Safari/Chrome): shows "Share -> Add to Home Screen" instructions (no install API on iOS)
 * - Banner shows from the 2nd pageview, ~3s after load; if dismissed, hidden for 30 days
 * - Any link with href ending in "#install-app" (e.g. footer menu item) triggers the install flow,
 *   bypassing the banner's pageview/dismissal gating
 * - Pushes funnel events to GTM dataLayer: pwa_session, pwa_banner_shown, pwa_install_clicked,
 *   pwa_installed, pwa_install_dismissed
 */
(function () {
  'use strict';

  var DISMISS_KEY = 'budli-pwa-dismissed-at';
  var PV_KEY = 'budli-pwa-pageviews';
  var INSTALLED_KEY = 'budli-pwa-installed';
  var DISMISS_DAYS = 30;
  var MIN_PAGEVIEWS = 2;
  var SHOW_DELAY_MS = 3000;

  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function track(eventName, params) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (params) for (var k in params) payload[k] = params[k];
    window.dataLayer.push(payload);
  }

  var isIos = /iPhone|iPad|iPod/.test(navigator.userAgent);
  var isIosChrome = isIos && /CriOS/.test(navigator.userAgent);
  var isIosSafari = isIos && /Safari/.test(navigator.userAgent) && !/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent);
  var platform = isIos ? 'ios' : (/Android/.test(navigator.userAgent) ? 'android' : 'desktop');

  // Already running as an installed app: count the session, do nothing else
  if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
    storageSet(INSTALLED_KEY, '1');
    try {
      if (!window.sessionStorage.getItem('budli-pwa-session-tracked')) {
        window.sessionStorage.setItem('budli-pwa-session-tracked', '1');
        track('pwa_session', { pwa_platform: platform });
      }
    } catch (e) {
      track('pwa_session', { pwa_platform: platform });
    }
    return;
  }

  var deferredPrompt = null;
  var banner = null;
  var stylesInjected = false;

  function iosShareHint() {
    return isIosChrome
      ? 'Tap the Share icon in the address bar, then “Add to Home Screen”'
      : 'Tap the Share icon below, then “Add to Home Screen”';
  }

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var css =
      '.pwa-banner{position:fixed;left:0;right:0;bottom:0;z-index:9999;display:flex;align-items:center;gap:1.2rem;' +
      'padding:1.2rem 1.5rem;padding-bottom:calc(1.2rem + env(safe-area-inset-bottom));background:#fff;' +
      'box-shadow:0 -0.2rem 1.2rem rgba(0,0,0,.15);font-size:1.4rem;line-height:1.4;' +
      'transform:translateY(110%);transition:transform .3s ease}' +
      '.pwa-banner--visible{transform:translateY(0)}' +
      '.pwa-banner__icon{width:4.4rem;height:4.4rem;border-radius:1rem;flex-shrink:0;border:1px solid rgba(0,0,0,.08)}' +
      '.pwa-banner__text{flex:1;min-width:0}' +
      '.pwa-banner__title{font-weight:700;margin:0 0 .2rem}' +
      '.pwa-banner__subtitle{margin:0;opacity:.75;font-size:1.2rem}' +
      '.pwa-banner__install{background:#c10077;color:#fff;border:0;border-radius:2.4rem;' +
      'padding:1rem 1.8rem;font-size:1.4rem;font-weight:700;cursor:pointer;flex-shrink:0}' +
      '.pwa-banner__close{background:none;border:0;font-size:2rem;line-height:1;padding:.6rem;' +
      'cursor:pointer;color:#666;flex-shrink:0}' +
      '@media screen and (min-width:750px){.pwa-banner{max-width:48rem;left:auto;right:2rem;bottom:2rem;border-radius:1.2rem;padding-bottom:1.2rem}}';
    var style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
  }

  function buildBanner(opts) {
    if (banner) return;
    injectStyles();
    banner = document.createElement('div');
    banner.className = 'pwa-banner';
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-label', 'Install the Budli app');

    var iconUrl = document.querySelector('link[rel="apple-touch-icon"]');
    var iconHtml = iconUrl ? '<img class="pwa-banner__icon" src="' + iconUrl.href + '" alt="" width="44" height="44" loading="lazy">' : '';

    banner.innerHTML =
      iconHtml +
      '<div class="pwa-banner__text">' +
      '<p class="pwa-banner__title">' + opts.title + '</p>' +
      '<p class="pwa-banner__subtitle">' + opts.subtitle + '</p>' +
      '</div>' +
      (opts.button ? '<button type="button" class="pwa-banner__install">' + opts.button + '</button>' : '') +
      '<button type="button" class="pwa-banner__close" aria-label="Close install banner">&times;</button>';

    document.body.appendChild(banner);

    banner.querySelector('.pwa-banner__close').addEventListener('click', function () {
      storageSet(DISMISS_KEY, String(Date.now()));
      track('pwa_install_dismissed', { pwa_platform: platform, pwa_trigger: opts.trigger });
      hideBanner();
    });

    var installBtn = banner.querySelector('.pwa-banner__install');
    if (installBtn && opts.onInstall) installBtn.addEventListener('click', opts.onInstall);

    track('pwa_banner_shown', { pwa_platform: platform, pwa_trigger: opts.trigger });
    setTimeout(function () { banner.classList.add('pwa-banner--visible'); }, 50);
    // Auto-hide after 8s so the banner never keeps covering the sticky buy bar.
    // Not treated as a dismissal: no 30-day snooze, may show again on a later pageview.
    setTimeout(function () { hideBanner(); }, 8000);
  }

  function hideBanner() {
    if (!banner) return;
    banner.classList.remove('pwa-banner--visible');
    setTimeout(function () { if (banner) { banner.remove(); banner = null; } }, 350);
  }

  function promptInstall(trigger) {
    if (!deferredPrompt) return;
    track('pwa_install_clicked', { pwa_platform: platform, pwa_trigger: trigger });
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then(function (choice) {
      if (choice.outcome === 'accepted') {
        storageSet(INSTALLED_KEY, '1');
      } else {
        storageSet(DISMISS_KEY, String(Date.now()));
        track('pwa_install_dismissed', { pwa_platform: platform, pwa_trigger: trigger });
      }
      deferredPrompt = null;
      hideBanner();
    });
  }

  function showAndroidBanner(trigger) {
    buildBanner({
      title: 'Get the Budli app',
      subtitle: 'One tap install — no Play Store needed',
      button: 'Install',
      trigger: trigger,
      onInstall: function () { promptInstall(trigger); }
    });
  }

  function showIosBanner(trigger) {
    buildBanner({
      title: 'Add Budli to your Home Screen',
      subtitle: iosShareHint(),
      button: null,
      trigger: trigger,
      onInstall: null
    });
  }

  // Manual trigger: any link pointing to #install-app (footer menu, page content, etc.)
  // Bypasses pageview/dismissal gating — the user explicitly asked for it.
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href$="#install-app"]');
    if (!link) return;
    event.preventDefault();
    if (deferredPrompt) {
      promptInstall('link');
    } else if (isIos) {
      hideBanner();
      showIosBanner('link');
    } else {
      hideBanner();
      buildBanner({
        title: 'Install the Budli app',
        subtitle: 'In your browser menu, choose “Install app” or “Add to Home Screen”',
        button: null,
        trigger: 'link',
        onInstall: null
      });
    }
  });

  window.addEventListener('appinstalled', function () {
    storageSet(INSTALLED_KEY, '1');
    track('pwa_installed', { pwa_platform: platform });
    hideBanner();
  });

  // Automatic banner: gated by pageviews and dismissal cap
  if (storageGet(INSTALLED_KEY)) return;

  var dismissedAt = parseInt(storageGet(DISMISS_KEY), 10);
  var dismissalActive = dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 864e5;

  var pageviews = (parseInt(storageGet(PV_KEY), 10) || 0) + 1;
  storageSet(PV_KEY, String(pageviews));
  var autoBannerAllowed = !dismissalActive && pageviews >= MIN_PAGEVIEWS;

  window.addEventListener('beforeinstallprompt', function (event) {
    event.preventDefault();
    deferredPrompt = event;
    // Auto banner is mobile-only; desktop users can still install via an explicit #install-app link
    if (autoBannerAllowed && platform === 'android') {
      setTimeout(function () { if (deferredPrompt && !banner) showAndroidBanner('auto'); }, SHOW_DELAY_MS);
    }
  });

  if (autoBannerAllowed && (isIosSafari || isIosChrome)) {
    setTimeout(function () { if (!banner) showIosBanner('auto'); }, SHOW_DELAY_MS);
  }
})();
